// Shared "is this match worth polling for live data right now" heuristic.
//
// The static match JSON's `status` field is only ever "NOT_STARTED" or
// "FINISHED" in practice (flipped by the GitHub Actions post-match sync),
// never "IN_PROGRESS" in real time - so there's no field to just check.
// Instead we guess from kickoff time: a match is a live *candidate* once
// its kickoff has passed and it hasn't been synced as finished yet. The
// candidate still has to be confirmed by an actual poll of
// /api/live-match (see useLiveMatches) before treating it as truly live,
// since kickoff can be delayed or the match may already be over by the
// time this window closes.
export const LIVE_WINDOW_MS = 3 * 60 * 60 * 1000; // 3 hours

export const isLiveCandidate = (match, now = new Date()) => {
  if (!match || match.status === "FINISHED" || !match.livescoreId) {
    return false;
  }
  const kickoff = new Date(match.date);
  const nowTime = now.getTime();
  return nowTime >= kickoff.getTime() && nowTime <= kickoff.getTime() + LIVE_WINDOW_MS;
};

export const getLiveCandidates = (allMatches, now = new Date()) =>
  allMatches.filter((match) => isLiveCandidate(match, now));

// live-match.js passes livescore's own status code straight through as
// `elapsed` while a match is in progress - normally a running clock like
// "62'", but "HT" specifically means the break between halves, which reads
// far better in Turkish than the bare code. Every other value (including
// unconfirmed live codes per that edge function's own header comment) is
// shown as-is.
export const formatElapsed = (elapsed) => (elapsed === "HT" ? "Devre Arası" : elapsed);

// Shared key builder so every caller (MatchList, standings, the match
// page) addresses the same useLiveMatches result the same way.
export const liveMatchKey = (match) => `${match.homeSlug || match.home?.slug}__${match.awaySlug || match.away?.slug}`;

// Overlays a currently-live scoreline onto the teams playing right now, as
// a *projected* line - played/points/GD/for/against as if the live score
// stood and the match were already over. Teams not currently playing pass
// through unchanged. Re-ranks by the same tie-break convention used
// throughout the stats pipeline (points, then goal difference, then goals
// for, then team name - see tools/team_stats/README.md), and keeps an
// `actualRank` alongside the new `displayRank` so callers rendering zone
// stripes can still key off the real season table rather than this
// projection. Shared by the standings page's Canlı tab and the homepage's
// own standings panel, so both stay in sync.
export const deriveLiveStandings = (standings, liveMatchesNow, liveResults) => {
  const liveByTeamFullName = {};
  liveMatchesNow.forEach((match) => {
    const live = liveResults[liveMatchKey(match)];
    if (!live) return;
    liveByTeamFullName[match.homeFullName] = { for: live.ftScore.home, against: live.ftScore.away };
    liveByTeamFullName[match.awayFullName] = { for: live.ftScore.away, against: live.ftScore.home };
  });

  const rows = standings.map((team) => {
    const liveLine = liveByTeamFullName[team.fullName];
    if (!liveLine) {
      return { ...team, actualRank: team.rank };
    }
    const goalsFor = team.for + liveLine.for;
    const goalsAgainst = team.against + liveLine.against;
    const pointsEarned = liveLine.for > liveLine.against ? 3 : liveLine.for === liveLine.against ? 1 : 0;
    return {
      ...team,
      played: team.played + 1,
      for: goalsFor,
      against: goalsAgainst,
      goalsDiff: goalsFor - goalsAgainst,
      points: team.points + pointsEarned,
      win: team.win + (liveLine.for > liveLine.against ? 1 : 0),
      draw: team.draw + (liveLine.for === liveLine.against ? 1 : 0),
      lose: team.lose + (liveLine.for < liveLine.against ? 1 : 0),
      actualRank: team.rank,
    };
  });

  rows.sort(
    (a, b) =>
      b.points - a.points ||
      b.goalsDiff - a.goalsDiff ||
      b.for - a.for ||
      a.fullName.localeCompare(b.fullName, "tr"),
  );
  rows.forEach((row, i) => {
    row.displayRank = i + 1;
  });
  return rows;
};
